import { useState } from "react";
import Button from "./Button";
import { Checkbox } from "./Checkbox";
import Input from "./Input";
import Modal from "./Modal";
import "./ActionDialog.css";

/*
  ActionDialog - the confirmation of ONE consequential action.

  It never performs the action itself: `onConfirm(reason)` calls the workflow's own API.
    * confirm stays disabled until the required reason / acknowledgement is given;
    * while the call runs everything is locked (no double submit, no Escape);
    * a failure keeps the dialog open and shows the server's message.

  reason   { label, required, min } -> a text area (omit for none); `min` = minimum length (default 3)
  ack      label of a checkbox that must be ticked (omit for none)
  labels   { confirm, cancel, reasonRequired, failed }; falls back to `t.action` (the Requests page text)
*/
export default function ActionDialog({ t, labels, title, description, children, reason, ack, confirmLabel, danger = false, onConfirm, onClose }) {
  const words = { ...(t?.action || {}), ...(labels || {}) };
  const [reasonText, setReasonText] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reasonOk = !reason?.required || reasonText.trim().length >= (reason.min || 3);
  const canConfirm = !busy && reasonOk && (!ack || acknowledged);

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(reasonText.trim());
    } catch (failure) {
      setError(failure?.message || words.failed);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      description={description}
      size="md"
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <Button variant={danger ? "danger" : "primary"} appearance="filled" disabled={!canConfirm} loading={busy} onClick={confirm}>
            {confirmLabel || words.confirm}
          </Button>
          <Button variant="neutral" appearance="soft" disabled={busy} onClick={onClose}>
            {words.cancelDialog || words.cancel}
          </Button>
        </>
      }
    >
      <div className="ui-action-dialog-body">
        {children}
        {reason && (
          <Input
            textarea
            label={reason.label}
            value={reasonText}
            onChange={(event) => setReasonText(event.target.value)}
            error={reason.required && reasonText.length > 0 && !reasonOk ? words.reasonRequired : undefined}
          />
        )}
        {ack && <Checkbox checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} label={ack} />}
        {error && (
          <p className="ui-action-dialog-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
